import { AutomateTask, success } from "@iflyrpa/share";
import { AxiosProxyParams, errorResponse } from "../../types";
import { Http } from "../../utils/http";
import type { SearchAccountInfoParams } from ".";
import { type douyinConfigData, type SearchAccountInfoResponse } from "./types";

/**
 * 抖音账号数据采集
 *
 * 核心接口：
 * 1. 用户完整信息: GET /aweme/v1/web/user/profile/self/
 *    返回 user.follower_count（粉丝）、user.following_count（关注）、user.total_favorited（获赞）等
 * 2. 近7日数据概览: GET /aweme/janus/creator/data/overview/all/?last_days_type=1
 *
 * 注意: 近7日数据接口需要 msToken / a_bogus 动态参数，
 * 建议通过 Playwright 复用已登录浏览器上下文请求（详见文档 3.2）。
 * 此处直接请求，若被风控拦截会返回对应错误，昨日/近7日数据降级为 0。
 */
export async function getDouyinData(
    _task: AutomateTask,
    params: SearchAccountInfoParams,
): Promise<SearchAccountInfoResponse> {
    try {
        // 验证关键 cookie（参考文档 3.1）
        const sessionidCookie = params.cookies.find((it) => it.name === "sessionid")?.value;
        if (!sessionidCookie) {
            return errorResponse("抖音账号数据异常，请重新绑定账号后重试。", 414);
        }

        // CSRF token（部分接口需要）
        const csrfToken = params.cookies.find((it) => it.name === "passport_csrf_token")?.value || "";

        const headers = {
            cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
            referer: "https://creator.douyin.com/creator-micro/home",
            origin: "https://creator.douyin.com",
            "user-agent":
                "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.36",
            ...(csrfToken ? { "x-secsdk-csrf-token": csrfToken } : {}),
        };

        const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.localIP, params.accountId];

        const http = new Http(...args);

        // 数值解析（文档中的计数字段为字符串，如 "12345"、"+10"）
        const toNum = (val: string | number | undefined | null): number => {
            if (val === undefined || val === null) return 0;
            const n = Number.parseInt(String(val).replace(/[+,]/g, ""), 10);
            return Number.isNaN(n) ? 0 : n;
        };

        // 1. 获取用户完整信息（粉丝数、关注数、获赞数、作品数等）
        const userProfileP = http
            .api<{
                status_code?: number;
                status_msg?: string;
                user?: {
                    follower_count?: number;
                    following_count?: number;
                    total_favorited?: number;
                    aweme_count?: number;
                    nickname?: string;
                    uid?: string;
                };
            }>(
                {
                    method: "get",
                    url: "https://www.douyin.com/aweme/v1/web/user/profile/self/",
                    params: {
                        device_platform: "webapp",
                        aid: "6383",
                        channel: "channel_pc_web",
                        pc_client_type: "1",
                        version_code: "170400",
                        version_name: "17.4.0",
                        cookie_enabled: "true",
                        platform: "PC",
                    },
                },
                { retries: 3, retryDelay: 100, timeout: 10000 },
            )
            .catch((e) => {
                _task.logger.warn(`抖音用户信息获取失败: ${e instanceof Error ? e.message : String(e)}`);
                return null;
            });

        // 2. 近7日数据概览
        const overviewP = http
            .api<{
                status_code?: number;
                data?: {
                    new_fans?: { last_period_incr?: string };
                    play?: { last_period_incr?: string };
                    digg?: { last_period_incr?: string };
                    comment?: { last_period_incr?: string };
                    share?: { last_period_incr?: string };
                    profile?: { last_period_incr?: string };
                };
            }>(
                {
                    method: "get",
                    url: "https://creator.douyin.com/aweme/janus/creator/data/overview/all/",
                    params: {
                        last_days_type: 1, // 近7日
                        aid: 2906,
                        app_name: "aweme_creator_platform",
                        device_platform: "web",
                    },
                },
                { retries: 3, retryDelay: 100, timeout: 5000 },
            )
            .catch((e) => {
                _task.logger.warn(
                    `抖音近7日数据获取失败（可能未开通数据中心或被风控）: ${e instanceof Error ? e.message : String(e)}`,
                );
                return null;
            });

        const [userProfile, overview] = await Promise.all([userProfileP, overviewP]);
        // _task.logger.info(`抖音用户信息响应: ${JSON.stringify(userProfile)}`);
        // _task.logger.info(`抖音近7日数据响应: ${JSON.stringify(overview)}`);

        // 从 API 响应提取统计数据
        let fansNum = 0;
        let favedNum = 0;
        let followingNum = 0;

        if (userProfile?.status_code === 0 && userProfile.user) {
            const user = userProfile.user;
            fansNum = toNum(user.follower_count);
            followingNum = toNum(user.following_count);
            favedNum = toNum(user.total_favorited);

            // _task.logger.info(
            //     `提取用户数据 - 昵称: ${user.nickname}, 粉丝: ${fansNum}, 关注: ${followingNum}, 获赞: ${favedNum}, 作品: ${user.aweme_count}`,
            // );
        } else {
            _task.logger.warn(
                `用户信息接口返回异常: status_code=${userProfile?.status_code}, msg=${userProfile?.status_msg}`,
            );
        }

        const ov = overview?.data;
        const douyinData: douyinConfigData = {
            fansNum, // 从 /aweme/v1/web/user/profile/self/ 接口提取
            favedNum, // 从 /aweme/v1/web/user/profile/self/ 接口提取
            fansNumLastWeek: toNum(ov?.new_fans?.last_period_incr),
            watchNumLastWeek: toNum(ov?.play?.last_period_incr),
            likeNumLastWeek: toNum(ov?.digg?.last_period_incr),
            commentNumLastWeek: toNum(ov?.comment?.last_period_incr),
            shareNumLastWeek: toNum(ov?.share?.last_period_incr),
            visitNumLastWeek: toNum(ov?.profile?.last_period_incr),
        };

        return success(douyinData, "抖音平台数据获取成功！");
    } catch (error) {
        // http 层抛出的是 ResponseData（{code, message, data}），
        // 直接透传以保留代理超时等场景的错误码与友好提示（如 599 / 414）
        if (error && typeof error === "object" && "code" in error && "message" in error) {
            const err = error as { code: number; message: string; data: unknown };
            _task.logger.error(`抖音账号数据获取失败: ${err.message} (${err.code})`);
            return errorResponse(err.message, err.code);
        }

        const errMsg = error instanceof Error ? error.message : String(error);
        _task.logger.error(`抖音账号数据获取失败: ${errMsg}`);
        return errorResponse(errMsg || "抖音账号数据获取失败");
    }
}
